/** 태블릿 마음 전환 휴식 QR — type=mindRest (v1) */

export type MindRestSessionResultQueryV1 = {
  v: 1;
  type: 'mindRest';
  sid: string;
  before: number;
  after: number;
  t: number | null;
  hl: string;
  sl: string;
  src: string;
  set: number | null;
};

/** 카카오 공유용 압축 토큰 페이로드 (`?mr=`) — ASCII only */
type MindRestSharePayloadV1 = {
  v: 1;
  sid: string;
  b: number;
  a: number;
  t?: number;
};

const DEFAULT_HEADLINE = '나의 마음 전환 휴식 기록';
const DEFAULT_SUBLINE = '사운드필 마음 전환 휴식 세션 결과입니다.';
const MOOD_MIN = 0;
const MOOD_MAX = 10;

function parseOptionalNumber(value: string | null): number | null {
  if (value == null || value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function decodeParam(value: string | null): string {
  if (value == null) return '';
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    return value;
  }
}

/** 기분 점수 0–10 (0=많이 힘들다, 10=편안하다) */
function parseMoodScore(value: string | null): number | null {
  const n = parseOptionalNumber(value);
  if (n == null) return null;
  return Math.max(MOOD_MIN, Math.min(MOOD_MAX, n));
}

export function isMindRestSessionQuery(search: string | URLSearchParams): boolean {
  const params =
    typeof search === 'string'
      ? new URLSearchParams(search.startsWith('?') ? search : `?${search}`)
      : search;
  if (params.get('mr')?.trim()) return true;
  return params.get('type')?.trim().toLowerCase() === 'mindrest';
}

/** 카카오 공유 링크용 — 한글·긴 쿼리 없이 ASCII 토큰 하나로 구분 */
export function encodeMindRestShareToken(data: {
  sid: string;
  before: number;
  after: number;
  t?: number | null;
}): string {
  const payload: MindRestSharePayloadV1 = {
    v: 1,
    sid: data.sid.trim(),
    b: data.before,
    a: data.after,
  };
  if (data.t != null && Number.isFinite(data.t)) {
    payload.t = Math.round(data.t);
  }
  const json = JSON.stringify(payload);
  const bytes = new TextEncoder().encode(json);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeMindRestShareToken(token: string): MindRestSharePayloadV1 | null {
  try {
    const normalized = token.trim().replace(/-/g, '+').replace(/_/g, '/');
    const pad =
      normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4));
    const binary = atob(normalized + pad);
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
    const parsed = JSON.parse(new TextDecoder().decode(bytes)) as MindRestSharePayloadV1;
    if (parsed?.v !== 1) return null;
    if (typeof parsed.sid !== 'string' || !parsed.sid.trim()) return null;
    if (!Number.isFinite(parsed.b) || !Number.isFinite(parsed.a)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function moodScorePercent(score: number): number {
  return Math.max(0, Math.min(100, (score / MOOD_MAX) * 100));
}

export function displayMoodScore(score: number): string {
  return String(Math.round(score));
}

/** URLSearchParams → 마음 전환 휴식 세션 결과 (필수 필드 없으면 null) */
export function parseMindRestSessionResultQuery(
  search: string | URLSearchParams,
): MindRestSessionResultQueryV1 | null {
  const params =
    typeof search === 'string'
      ? new URLSearchParams(search.startsWith('?') ? search : `?${search}`)
      : search;

  // 카카오 공유 단축 링크: ?mr=<token>
  const mrToken = params.get('mr')?.trim();
  if (mrToken) {
    const payload = decodeMindRestShareToken(mrToken);
    if (!payload) return null;
    const before = parseMoodScore(String(payload.b));
    const after = parseMoodScore(String(payload.a));
    if (before == null || after == null) return null;
    return {
      v: 1,
      type: 'mindRest',
      sid: payload.sid.trim(),
      before,
      after,
      t: payload.t != null && Number.isFinite(payload.t) ? payload.t : null,
      hl: DEFAULT_HEADLINE,
      sl: DEFAULT_SUBLINE,
      src: 'kakao',
      set: null,
    };
  }

  if (params.get('v') !== '1') return null;
  if (params.get('type')?.trim().toLowerCase() !== 'mindrest') return null;

  const sid = decodeParam(params.get('sid')).trim();
  if (!sid) return null;

  const before = parseMoodScore(params.get('before'));
  const after = parseMoodScore(params.get('after'));
  if (before == null || after == null) return null;

  return {
    v: 1,
    type: 'mindRest',
    sid,
    before,
    after,
    t: parseOptionalNumber(params.get('t')),
    hl: decodeParam(params.get('hl')).trim() || DEFAULT_HEADLINE,
    sl: decodeParam(params.get('sl')).trim() || DEFAULT_SUBLINE,
    src: decodeParam(params.get('src')).trim() || 'tablet',
    set: parseOptionalNumber(params.get('set')),
  };
}
